import type {JobsData, AgencyList} from 'src/types/referrer-portal';
import type {ReferralPrograms} from 'src/types/referral-v2';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {
  fetchJobDescription,
  getAgencyPrograms,
} from 'src/action-creators/referrer-portal/user-form';
import {getReferralSocialLinks} from 'src/action-creators/referrer-portal/dashboard';
import {selectSocialLinks} from 'src/selectors/referrer-portal';
import {isReferrerPortal} from 'src/components/referral-v2/common';
import {
  type GenericHeaderItem,
  BasicSingleCell,
  Link,
  Panel,
  PanelHeader,
  SubTitleLarge,
  BodyMedium,
  Chip,
  Truncate,
  TruncatedTextWithTooltip,
} from '@spaced-out/ui-design-system/lib/components/index';
import JobDetails from 'src/components/referrer-portal/user-form/job-details';
import css from 'src/components/referrer-portal/jobs/jobs.css';


export const getHeaders = (
  t: (arg1: string, arg2: string) => string,
  showRefIdColumn?: boolean,
  referralPayoutEnabled?: boolean,
): GenericHeaderItem<
  JobsData,
  {
    programId: string;
    agencyId: string;
  } /*Extra argument type*/
>[] => {
  const headers = [
    {
      key: 'title',
      label: t('Job', 'Job'),
      sortable: true,
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type. | TS7031 - Binding element 'extras' implicitly has an 'any' type.
      render: ({data, extras}) => {
        return <JobTitle data={data} agencyId={extras?.agencyId ?? ''} />;
      },
    },
    {
      key: 'location',
      label: t('Location', 'Location'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell className={css.tableColumn}>
          <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 20}}>
            <BodyMedium>
              {data.location !== '' ? data.location : '-'}
            </BodyMedium>
          </TruncatedTextWithTooltip>
        </BasicSingleCell>
      ),
    },
    {
      key: 'id',
      label: t('Job_Id', 'Job Id'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell>
          <BodyMedium>{data.id ?? '-'}</BodyMedium>
        </BasicSingleCell>
      ),
    },
    ...(showRefIdColumn
      ? [
          {
            key: 'job_reference_id',
            label: t('Ref_Number', 'Ref Number'),
            // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
            render: ({data}) => (
              <BasicSingleCell className={css.tableColumn}>
                <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 20}}>
                  <BodyMedium>{data.job_reference_id ?? '-'}</BodyMedium>
                </TruncatedTextWithTooltip>
              </BasicSingleCell>
            ),
          },
        ]
      : []),
    ...(referralPayoutEnabled
      ? [
          {
            key: 'bonus',
            label: t('Bonus', 'Bonus'),
            // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
            render: ({data}) => (
              <BasicSingleCell>
                <BodyMedium>{data.bonus.amount ?? '-'}</BodyMedium>
              </BasicSingleCell>
            ),
          },
          {
            key: 'payout_period',
            label: t('Payout_Period', 'Payout Period'),
            // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
            render: ({data}) => (
              <BasicSingleCell>
                <BodyMedium>{data.payout_period ?? '-'}</BodyMedium>
              </BasicSingleCell>
            ),
          },
        ]
      : []),
    {
      key: 'refer',
      label: '',
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type. | TS7031 - Binding element 'extras' implicitly has an 'any' type.
      render: ({data, extras}) => (
        <ReferCandidate
          jobId={data.id}
          programId={extras?.programId ?? ''}
          agencyId={extras?.agencyId ?? ''}
          jobTitle={data.title}
        />
      ),
    },
  ];

  // @ts-expect-error - TS2322 - Type '({ key: string; label: string; sortable: boolean; render: ({ data, extras }: { data: any; extras: any; }) => Element; } | { key: string; label: string; render: ({ data, extras }: { data: any; extras: any; }) => Element; sortable?: undefined; })[]' is not assignable to type 'GenericHeaderItem<JobsData, { programId: string; agencyId: string; }>[]'.
  return headers;
};

export const JobTitle = ({
  data,
  agencyId,
}: {
  data: JobsData;
  agencyId: string;
}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const [showJD, setShowJD] = React.useState(false);

  const isDescriptionLoading = useAsyncDependency(() => {
    const apis: Array<any> = [];
    if (showJD) {
      apis.push(dispatch(fetchJobDescription(agencyId, data.id)));
    }
    return Promise.all(apis);
  }, [showJD]);

  return (
    <>
      <Panel
        anchor="right"
        initialFocus={2}
        onClose={() => setShowJD(false)}
        hideBackdrop={false}
        isOpen={showJD}
      >
        <PanelHeader onCloseButtonClick={() => setShowJD(false)}>
          <SubTitleLarge>{t('Job_Detail', 'Job Detail')}</SubTitleLarge>
        </PanelHeader>
        <JobDetails jobId={data.id} isDataLoading={isDescriptionLoading} />
      </Panel>
      <BasicSingleCell className={css.tableColumn}>
        <Link as="buttonTextMedium" onClick={() => setShowJD(true)}>
          <Truncate
            className={css.truncate}
            line={1}
            showFullTextOnHover={true}
            // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
            className={css.truncate}
          >
            {data.title}
          </Truncate>
        </Link>
      </BasicSingleCell>
    </>
  );
};

export const ReferCandidate = ({
  jobId,
  jobTitle,
  programId,
  agencyId,
}: {
  jobId: string;
  jobTitle: string;
  programId: string;
  agencyId: string;
}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = React.useState(false);

  const isDataLoading = useAsyncDependency(() => {
    const apis: Array<any> = [];
    if (isOpen) {
      apis.push(dispatch(getReferralSocialLinks()));
    }
    if (isReferrerPortal()) {
      apis.push(dispatch(getAgencyPrograms(agencyId)));
    }
    return Promise.all(apis);
  }, [isOpen]);

  React.useEffect(() => {
    const handlePostMessage = (event: any) => {
      if (event?.data?.type === 'CLOSE_REFER_NOW_PANEL') {
        setIsOpen(event.data.showReferNowPanel);
      }
    };
    window.addEventListener('message', handlePostMessage);
    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, []);

  const socialLinks = useSelector(selectSocialLinks);

  const agencyList: AgencyList[] | null | undefined = useSelector(
    (state: any) => state?.referrerPortal?.agency_list,
  );

  const programList: ReferralPrograms[] | null | undefined = useSelector(
    (state: any) => state?.referrerPortal?.agency_programs,
  );

  const selectedAgencyName = agencyList?.find(
    (agency: AgencyList) => agency.id === agencyId,
  )?.name;

  const selectedProgramName = programList?.find(
    (program: ReferralPrograms) => program.id.toString() === programId,
  )?.name;

  const queryString = `iframe=true&agency_id=${agencyId}&agency_name=${
    selectedAgencyName ?? ''
  }&program_id=${programId}&program_name=${
    selectedProgramName ?? ''
  }&job_id=${jobId}&job_title=${jobTitle}`;

  return (
    <BasicSingleCell>
      <Chip
        disabled={isDataLoading}
        onClick={() => setIsOpen(true)}
        semantic="information"
      >
        {t('Refer_Now', 'Refer Now')}
      </Chip>
      <Panel
        isOpen={isOpen && !isDataLoading}
        tapOutsideToClose
        onClose={() => setIsOpen(false)}
        anchor="right"
        size="large"
        hideBackdrop={false}
      >
        <iframe
          src={`${socialLinks?.long_url || ''}?${queryString}`}
          width="100%"
          height="100%"
          frameBorder="0"
        />
      </Panel>
    </BasicSingleCell>
  );
};
